import type { ExtractedAnswer } from "../types";

interface DdAnswerInputProps {
  effectiveAnswer: ExtractedAnswer;
  fileId: string;
  isSaved: boolean;
  isSkipped: boolean;
  onUpdateAnswer: (fileId: string, questionId: number, field: keyof ExtractedAnswer, value: any) => void;
}

export function DdAnswerInput({
  effectiveAnswer,
  fileId,
  isSaved,
  isSkipped,
  onUpdateAnswer,
}: DdAnswerInputProps) {
  if (isSkipped) {
    return (
      <p className="text-[10px] text-neutral-400 italic mt-1">
        Deze vraag wordt overgeslagen bij opslaan
      </p>
    );
  }

  if (effectiveAnswer.answerType === "options") {
    return (
      <div className="space-y-1">
        {effectiveAnswer.matchedOptionLabel ? (
          <span className="text-xs text-neutral-primary">
            {effectiveAnswer.matchedOptionLabel}
          </span>
        ) : (
          <span className="text-xs text-neutral-secondary-mid italic">
            Geen optie geselecteerd
          </span>
        )}
        {effectiveAnswer.availableOptions &&
          effectiveAnswer.availableOptions.length > 0 &&
          !isSaved && (
            <select
              value={effectiveAnswer.matchedOptionId ?? ""}
              onChange={(e) => {
                const optionId = e.target.value ? parseInt(e.target.value) : null;
                const option = effectiveAnswer.availableOptions?.find((o) => o.id === optionId);
                onUpdateAnswer(fileId, effectiveAnswer.questionId, "matchedOptionId", optionId);
                onUpdateAnswer(fileId, effectiveAnswer.questionId, "matchedOptionLabel", option?.label ?? null);
              }}
              className="mt-1 block w-full text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            >
              <option value="">Selecteer optie...</option>
              {effectiveAnswer.availableOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
      </div>
    );
  }

  if (effectiveAnswer.answerType === "free_text") {
    return (
      <div className="space-y-1">
        {isSaved ? (
          <span className="text-xs text-neutral-primary">
            {effectiveAnswer.extractedValue as string}
          </span>
        ) : (
          <textarea
            value={(effectiveAnswer.extractedValue as string) ?? ""}
            onChange={(e) => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", e.target.value)}
            className="w-full text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            rows={2}
          />
        )}
      </div>
    );
  }

  if (effectiveAnswer.answerType === "date") {
    return (
      <div className="space-y-1">
        {isSaved ? (
          <span className="text-xs text-neutral-primary">
            {effectiveAnswer.extractedValue as string}
          </span>
        ) : (
          <input
            type="date"
            value={(effectiveAnswer.extractedValue as string) ?? ""}
            onChange={(e) => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", e.target.value)}
            className="text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        )}
      </div>
    );
  }

  if (effectiveAnswer.answerType === "number") {
    return (
      <div className="space-y-1">
        {isSaved ? (
          <span className="text-xs text-neutral-primary">
            {effectiveAnswer.extractedValue as number}
          </span>
        ) : (
          <input
            type="number"
            value={(effectiveAnswer.extractedValue as number) ?? ""}
            onChange={(e) =>
              onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", e.target.value ? parseFloat(e.target.value) : null)
            }
            className="text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
        )}
      </div>
    );
  }

  if (effectiveAnswer.answerType === "boolean") {
    return (
      <div className="space-y-1">
        {isSaved ? (
          <span className="text-xs text-neutral-primary">
            {effectiveAnswer.extractedValue === true ? "Ja" : effectiveAnswer.extractedValue === false ? "Nee" : "Onbekend"}
          </span>
        ) : (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="radio"
                name={`bool-${fileId}-${effectiveAnswer.questionId}`}
                checked={effectiveAnswer.extractedValue === true}
                onChange={() => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", true)}
                className="text-violet-600"
              />
              Ja
            </label>
            <label className="flex items-center gap-1 text-xs cursor-pointer">
              <input
                type="radio"
                name={`bool-${fileId}-${effectiveAnswer.questionId}`}
                checked={effectiveAnswer.extractedValue === false}
                onChange={() => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", false)}
                className="text-violet-600"
              />
              Nee
            </label>
          </div>
        )}
      </div>
    );
  }

  if (effectiveAnswer.answerType === "extraction") {
    if (effectiveAnswer.extractionValueType === "number") {
      return (
        <div className="space-y-1">
          {isSaved ? (
            <span className="text-xs text-neutral-primary">{effectiveAnswer.extractedValue as number}</span>
          ) : (
            <input
              type="number"
              value={(effectiveAnswer.extractedValue as number) ?? ""}
              onChange={(e) =>
                onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", e.target.value ? parseFloat(e.target.value) : null)
              }
              className="w-full text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
              placeholder="Voer getal in..."
            />
          )}
        </div>
      );
    }
    if (effectiveAnswer.extractionValueType === "date") {
      return (
        <div className="space-y-1">
          {isSaved ? (
            <span className="text-xs text-neutral-primary">{effectiveAnswer.extractedValue as string}</span>
          ) : (
            <input
              type="date"
              value={(effectiveAnswer.extractedValue as string) ?? ""}
              onChange={(e) => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", e.target.value)}
              className="text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            />
          )}
        </div>
      );
    }
    if (effectiveAnswer.extractionValueType === "boolean") {
      return (
        <div className="space-y-1">
          {isSaved ? (
            <span className="text-xs text-neutral-primary">
              {effectiveAnswer.extractedValue === true ? "Ja" : effectiveAnswer.extractedValue === false ? "Nee" : "Onbekend"}
            </span>
          ) : (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="radio"
                  name={`extraction-bool-${fileId}-${effectiveAnswer.questionId}`}
                  checked={effectiveAnswer.extractedValue === true}
                  onChange={() => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", true)}
                  className="text-violet-600"
                />
                Ja
              </label>
              <label className="flex items-center gap-1 text-xs cursor-pointer">
                <input
                  type="radio"
                  name={`extraction-bool-${fileId}-${effectiveAnswer.questionId}`}
                  checked={effectiveAnswer.extractedValue === false}
                  onChange={() => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", false)}
                  className="text-violet-600"
                />
                Nee
              </label>
            </div>
          )}
        </div>
      );
    }
    // String extraction (default)
    return (
      <div className="space-y-1">
        {isSaved ? (
          <span className="text-xs text-neutral-primary">{effectiveAnswer.extractedValue as string}</span>
        ) : (
          <textarea
            value={(effectiveAnswer.extractedValue as string) ?? ""}
            onChange={(e) => onUpdateAnswer(fileId, effectiveAnswer.questionId, "extractedValue", e.target.value)}
            className="w-full text-xs border border-violet-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-violet-500"
            rows={2}
            placeholder="Voer tekst in..."
          />
        )}
      </div>
    );
  }

  return (
    <span className="text-xs text-neutral-secondary-mid">
      {String(effectiveAnswer.extractedValue ?? "-")}
    </span>
  );
}
